import axios, { AxiosResponse, AxiosError } from 'axios';
import { Environment } from 'global/environment';
import { OpenAPI, ApiError } from 'common/api';
import { handleHttpError, handleServiceError } from './handleError';
import { RequestConfig } from './types';

OpenAPI.BASE = Environment.BACKEND_API;

export const axiosInstance = axios.create();

export async function serviceRequest<T>(
  apiRequest: () => Promise<T>
): Promise<T>;
export async function serviceRequest<T, Data>(
  apiRequest: () => Promise<T>,
  mapResponse?: (response: T) => Data
): Promise<Data>;
export async function serviceRequest<T, Data>(
  apiRequest: () => Promise<T>,
  mapResponse?: (response: T) => Data
) {
  try {
    const data = await apiRequest();
    return mapResponse ? mapResponse(data) : data;
  } catch (err: unknown) {
    return Promise.reject(handleServiceError(err as ApiError));
  }
}

function makeHttpRequest<T>(
  apiCall: () => Promise<AxiosResponse<T>>,
  mapData?: (data: AxiosResponse<T>['data']) => T
): Promise<T> {
  return apiCall()
    .then((data: AxiosResponse<T>) =>
      mapData ? mapData(data.data) : (data.data as T)
    )
    .catch((e: unknown) => Promise.reject(handleHttpError(e as AxiosError)));
}

export function getRequest<T>(
  path: string,
  config?: RequestConfig<T>
): Promise<T> {
  return makeHttpRequest<T>(
    () => axiosInstance.get(path, config?.options),
    config?.mapData
  );
}

export function postRequest<T>(
  path: string,
  data: any,
  config?: RequestConfig<T>
): Promise<T> {
  return makeHttpRequest<T>(
    () => axiosInstance.post(path, data, config?.options),
    config?.mapData
  );
}

export function putRequest<T>(
  path: string,
  data: any,
  config?: RequestConfig<T>
): Promise<T> {
  return makeHttpRequest<T>(
    () => axiosInstance.put(path, data, config?.options),
    config?.mapData
  );
}

export function deleteRequest<T>(
  path: string,
  config?: RequestConfig<T>
): Promise<T> {
  return makeHttpRequest<T>(
    () => axiosInstance.delete(path, config?.options),
    config?.mapData
  );
}
